import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  UserCog, Plus, Trash2, CalendarClock, Loader2, AlertTriangle, ShieldCheck,
  ArrowRight, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const isLeader = (u) =>
  u?.role === "admin" ||
  u?.role === "crew_chief" ||
  ["Supervisor", "Manager", "Senior Manager"].includes(u?.job_title);

function fmtDay(iso) {
  try { return new Date(iso + "T12:00:00").toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" }); }
  catch { return iso; }
}

function todayISO() { return new Date().toISOString().slice(0, 10); }

/**
 * Self-service Out-of-office delegate manager. Rendered on /profile for
 * Supervisor / Manager / Senior Manager / Crew Chief / Admin. Non-leaders
 * do not see the card at all.
 */
export default function OutOfOfficeDelegateCard() {
  const { user } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ delegate_id: "", start_date: todayISO(), end_date: todayISO(), notes: "" });
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: mine }] = await Promise.all([
        api.get("/delegations/candidates"),
        api.get("/delegations/mine"),
      ]);
      setCandidates(c);
      setOutgoing(mine.outgoing || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load delegations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isLeader(user)) load(); }, [user]);

  if (!isLeader(user)) return null;

  const submit = async () => {
    if (!form.delegate_id) { toast.error("Pick a delegate"); return; }
    if (!form.start_date || !form.end_date) { toast.error("Pick start + end dates"); return; }
    if (form.end_date < form.start_date) { toast.error("End date must be on or after start"); return; }
    setCreating(true);
    try {
      await api.post("/delegations/self", {
        delegate_id: form.delegate_id,
        start_date: form.start_date,
        end_date: form.end_date,
        notes: form.notes?.trim() || null,
      });
      toast.success("Delegate saved");
      setForm({ delegate_id: "", start_date: todayISO(), end_date: todayISO(), notes: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this delegation? Your delegate will lose approval powers immediately.")) return;
    try {
      await api.delete(`/delegations/self/${id}`);
      toast.success("Delegation removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    }
  };

  const isActive = (d) => todayISO() >= d.start_date && todayISO() <= d.end_date;

  return (
    <section className="mt-8 border border-summit-hairline rounded-lg bg-white p-6" data-testid="ooo-delegate-card">
      <div className="flex items-center gap-2 mb-4">
        <UserCog className="w-4 h-4 text-summit-primary" />
        <h2 className="font-display text-lg font-medium tracking-tight">Out-of-office delegate</h2>
      </div>
      <p className="text-xs text-gray-500 mb-5 max-w-lg">
        Pick another leader to approve timesheets on your behalf while you&apos;re away. They&apos;ll see
        your pending queue and be able to approve at your level for the dates you set.
      </p>

      {error && (
        <div className="mb-4 border border-red-200 bg-red-50 text-red-800 rounded-md p-3 flex items-center gap-2 text-xs" data-testid="ooo-error">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* Current delegations */}
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
          Current &amp; upcoming
        </div>
        {loading ? (
          <div className="text-xs text-gray-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>
        ) : outgoing.length === 0 ? (
          <div className="text-xs text-gray-400 italic" data-testid="ooo-empty">
            No delegations set. Pick one below when you&apos;re heading out.
          </div>
        ) : (
          <div className="space-y-2" data-testid="ooo-list">
            {outgoing.map((d) => {
              const active = isActive(d);
              return (
                <div
                  key={d.id}
                  className={`flex items-start justify-between gap-3 px-3 py-2 border rounded-md ${
                    active ? "border-emerald-300 bg-emerald-50" : "border-summit-hairline bg-summit-glacier-soft/50"
                  }`}
                  data-testid={`ooo-delegation-${d.id}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{d.delegate_name}</span>
                      {active ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-emerald-300 bg-white text-emerald-800 font-mono text-[10px] uppercase tracking-widest">
                          <ShieldCheck className="w-3 h-3" /> Active now
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-summit-hairline bg-white text-gray-600 font-mono text-[10px] uppercase tracking-widest">
                          Upcoming
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-[11px] text-gray-600 mt-1 flex items-center gap-1.5">
                      <CalendarClock className="w-3 h-3" />
                      {fmtDay(d.start_date)} <ArrowRight className="w-3 h-3" /> {fmtDay(d.end_date)}
                    </div>
                    {d.notes && <div className="text-xs text-gray-500 mt-1 italic">“{d.notes}”</div>}
                  </div>
                  <button
                    onClick={() => remove(d.id)}
                    className="p-1.5 rounded hover:bg-white text-red-600"
                    aria-label="Remove"
                    title="Remove delegation"
                    data-testid={`ooo-delete-${d.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-summit-hairline pt-5">
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-3">
          Add new delegation
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
          <div className="md:col-span-2">
            <Label className="text-xs font-mono uppercase tracking-widest text-gray-500">Delegate</Label>
            <Select value={form.delegate_id} onValueChange={(v) => setForm((f) => ({ ...f, delegate_id: v }))}>
              <SelectTrigger className="mt-1 border-summit-hairline" data-testid="ooo-delegate-picker">
                <SelectValue placeholder={candidates.length ? "Pick a leader" : "No leaders available"} />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.display_name} · {c.job_title}
                    {c.department ? ` · ${c.department}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-gray-500">Start</Label>
            <Input
              type="date"
              value={form.start_date}
              onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
              className="mt-1 border-summit-hairline"
              data-testid="ooo-start"
            />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-gray-500">End</Label>
            <Input
              type="date"
              value={form.end_date}
              onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
              className="mt-1 border-summit-hairline"
              data-testid="ooo-end"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-mono uppercase tracking-widest text-gray-500">Note (optional)</Label>
            <Input
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="e.g. On vacation — please handle Bartle's timesheet"
              className="mt-1 border-summit-hairline"
              data-testid="ooo-note"
            />
          </div>
        </div>
        <div className="mt-5">
          <Button
            onClick={submit}
            disabled={creating || !form.delegate_id || !form.start_date || !form.end_date}
            className="bg-summit-primary hover:bg-summit-primary-hover text-white"
            data-testid="ooo-submit"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            <span className="ml-2 text-sm">{creating ? "Saving…" : "Save delegation"}</span>
          </Button>
        </div>
      </div>
    </section>
  );
}
